// Angular
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
// Translate
import { TranslateModule } from '@ngx-translate/core';
import { PartialsModule } from '../../partials/partials.module';
// Services
import {
  HttpUtilsService,
  TypesUtilsService,
  InterceptService,
  LayoutUtilsService,
} from '../../../core/_base/crud';
// Shared
import {
  ActionNotificationComponent,
  ConfirmActionDialogComponent,
  DeleteEntityDialogComponent,
  FetchEntityDialogComponent,
  UpdateStatusDialogComponent,
} from '../../partials/content/crud';
// Components
import { AgreementManagementComponent } from './agreement-management.component';
import { AgreementEditDialogComponent } from './agreement/agreement-edit/agreement-edit.dialog.component';
// NGRX
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
// Material
import { ModuleGuard } from '../../../core/auth';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatRadioModule } from '@angular/material/radio';
import { MatIconModule } from '@angular/material/icon';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCardModule } from '@angular/material/card';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialogModule, MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompaniesService } from 'src/app/core/management/_services';
import { NgxPermissionsModule } from 'ngx-permissions';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { InlineSVGModule } from 'ng-inline-svg';
import { FilteredViewInterceptor } from '../../../core/auth/_helpers/filtered-view.interceptor';
import { AuthInterceptor } from '../auth/auth.interceptor';
import { TwixorService } from '../../../core/services/twixor.service';
import { ImageCropperModule } from 'ngx-image-cropper';

import { MatFormFieldModule } from '@angular/material/form-field';

import { AgreementListComponent } from './agreement/agreement-list/agreement-list.component';
import { AgreementTemplateListComponent } from './agreement/template-list/template-list.component';
import {
  AgreementEffects,
  agreementsReducer,
  AgreementTemplateEffects,
  agreementTemplatesReducer,
} from 'src/app/core/agreement';
import { AgreementTemplateEditComponent } from './agreement/template-edit/template-edit.component';
import { ConfirmRevokeDialogComponent } from './agreement/agreement-list/confirm-revoke-dialog/confirm-revoke-dialog.component';
import { TemplateEditDialogComponent } from './agreement/template-edit/template-edit-dialog/template-edit-dialog';
import { CoreModule } from 'src/app/core/core.module';
import { PdfViewDialogComponent } from './agreement/pdf-view/pdf-view';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { SharedModule } from 'src/app/shared/shared.module';

const routes: Routes = [
  {
    path: '',
    component: AgreementManagementComponent,
    children: [
      {
        path: '',
        redirectTo: 'list',
        pathMatch: 'full',
      },
      {
        path: 'list',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToAgreementModule' },
        component: AgreementListComponent,
      },
      {
        path: 'templates',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToAgreementModule' },
        component: AgreementTemplateListComponent,
      },
    ],
  },
];

@NgModule({
  imports: [
    CommonModule,
    NgxExtendedPdfViewerModule,
    HttpClientModule,
    PartialsModule,
    RouterModule.forChild(routes),
    FormsModule,
    ReactiveFormsModule,
    TranslateModule.forChild(),
    StoreModule.forFeature('agreements', agreementsReducer),
    EffectsModule.forFeature([AgreementEffects]),
    StoreModule.forFeature('agreementTemplates', agreementTemplatesReducer),
    EffectsModule.forFeature([AgreementTemplateEffects]),
    MatButtonModule,
    MatMenuModule,
    MatSelectModule,
    MatInputModule,
    MatFormFieldModule,
    MatTableModule,
    MatAutocompleteModule,
    MatRadioModule,
    MatIconModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatDatepickerModule,
    MatCardModule,
    MatPaginatorModule,
    MatSortModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatExpansionModule,
    MatTabsModule,
    MatTooltipModule,
    MatDialogModule,
    MatSlideToggleModule,
    NgxPermissionsModule.forChild(),
    InlineSVGModule,
    ImageCropperModule,
    SharedModule,
    CoreModule,
  ],
  providers: [
    InterceptService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InterceptService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FilteredViewInterceptor,
      multi: true,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        hasBackdrop: true,
        panelClass: 'kt-mat-dialog-container__wrapper',
        height: 'auto',
        width: '900px',
      },
    },
    HttpUtilsService,
    TypesUtilsService,
    LayoutUtilsService,
    CompaniesService,
    TwixorService,
  ],
  entryComponents: [
    ActionNotificationComponent,
    ConfirmActionDialogComponent,
    DeleteEntityDialogComponent,
    UpdateStatusDialogComponent,
    FetchEntityDialogComponent,
    AgreementEditDialogComponent,
    ConfirmRevokeDialogComponent,
    PdfViewDialogComponent,
    TemplateEditDialogComponent,
    AgreementTemplateEditComponent,
  ],
  declarations: [
    AgreementManagementComponent,
    AgreementListComponent,
    AgreementEditDialogComponent,
    AgreementTemplateListComponent,
    ConfirmRevokeDialogComponent,
    PdfViewDialogComponent,
    TemplateEditDialogComponent,
    AgreementTemplateEditComponent,
  ],
  exports: [MatProgressSpinnerModule],
})
export class AgreementManagementModule {}
